import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, off, push, set, update } from 'firebase/database';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { db } from '..';
const API_URL = 'https://api.test.frontforcecrm.com'

const ADMIN_ID = '1111';
const CHAT_UPLOAD_URL = `${API_URL}/api/chat/upload-attachments`;

const isDeletedForEveryone = message => {
  return message?.deletedForEveryone === true;
};

const isDeletedForAdmin = message => {
  return (
    message?.deletedFor?.[ADMIN_ID] === true ||
    message?.deletedFor?.[String(ADMIN_ID)] === true
  );
};

const getSenderId = message => {
  return String(
    message?.user?._id ||
      message?.sender_id ||
      message?.senderId ||
      ''
  );
};

const getReceiverId = message => {
  return String(
    message?.receiver?._id ||
      message?.receiver_id ||
      message?.receiverId ||
      ''
  );
};

const getVisibleText = message => {
  if (isDeletedForEveryone(message)) return 'This message was deleted';
  return message?.text || '';
};

const formatDate = date => {
  if (!date) return '';

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return '';

  return parsedDate.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getElectricianIdFromMessages = messages => {
  const incoming = messages.find(message => {
    const senderId = getSenderId(message);
    const senderRole = String(message?.user?.role || '').toLowerCase();

    return (
      senderId &&
      senderId !== String(ADMIN_ID) &&
      senderRole === 'electrician'
    );
  });

  if (incoming) return getSenderId(incoming);

  const outgoing = messages.find(message => {
    const receiverId = getReceiverId(message);
    const receiverRole = String(message?.receiver?.role || '').toLowerCase();

    return receiverId && receiverRole === 'electrician';
  });

  if (outgoing) return getReceiverId(outgoing);

  const anyNonAdminSender = messages.find(message => {
    const senderId = getSenderId(message);
    return senderId && senderId !== String(ADMIN_ID);
  });

  if (anyNonAdminSender) return getSenderId(anyNonAdminSender);

  const anyNonAdminReceiver = messages.find(message => {
    const receiverId = getReceiverId(message);
    return receiverId && receiverId !== String(ADMIN_ID);
  });

  if (anyNonAdminReceiver) return getReceiverId(anyNonAdminReceiver);

  return '';
};

const createPreviewUrl = file => {
  if (!file) return '';
  return URL.createObjectURL(file);
};

const ChatHistory = ({
  jobId,
  senderId = ADMIN_ID,
  electricianId,
  electricianName = 'Electrician',
}) => {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [resolvedElectricianId, setResolvedElectricianId] = useState(
    electricianId || ''
  );
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);

  const messageListRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const uploadXhrRefs = useRef({});

  const adminId = String(senderId || ADMIN_ID);
  const resolvedJobId = String(jobId || '');
  const chatPath = `adminMessageChat/${resolvedJobId}`;

  useEffect(() => {
    return () => {
      Object.values(uploadXhrRefs.current || {}).forEach(xhr => {
        try {
          xhr?.abort?.();
        } catch (abortError) {
          console.error('Upload cleanup abort error:', abortError);
        }
      });

      uploadXhrRefs.current = {};

      pendingAttachments.forEach(file => {
        if (file?.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!resolvedJobId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const chatRef = ref(db, chatPath);

    const onMessage = onValue(
      chatRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = Object.entries(snapshot.val())
            .map(([firebaseKey, value]) => ({
              firebaseKey,
              ...value,
            }))
            .filter(message => !isDeletedForAdmin(message))
            .sort((a, b) => {
              const aTime = new Date(a?.createdAt || 0).getTime();
              const bTime = new Date(b?.createdAt || 0).getTime();

              return aTime - bTime;
            });

          setMessages(data);

          const detectedElectricianId =
            electricianId || getElectricianIdFromMessages(data);

          if (detectedElectricianId) {
            setResolvedElectricianId(String(detectedElectricianId));
          }
        } else {
          setMessages([]);
        }

        setLoading(false);

        setTimeout(() => {
          if (messageListRef.current) {
            messageListRef.current.scrollTop =
              messageListRef.current.scrollHeight;
          }
        }, 80);
      },
      err => {
        setError(`Failed to fetch messages: ${err.message}`);
        setLoading(false);
      }
    );

    return () => off(chatRef, 'value', onMessage);
  }, [resolvedJobId, electricianId, chatPath]);

  const addFilesToPending = fileList => {
    const files = Array.from(fileList || []).filter(file =>
      String(file?.type || '').startsWith('image/')
    );

    if (files.length === 0) return;

    const nextFiles = files.map(file => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: createPreviewUrl(file),
      name: file.name || `chat-image-${Date.now()}.jpg`,
      type: file.type || 'image/jpeg',
      status: 'queued',
      progress: 0,
      serverUrl: '',
      error: '',
    }));

    setPendingAttachments(prev => [...prev, ...nextFiles]);
  };

  const handleGalleryChange = event => {
    addFilesToPending(event.target.files);
    event.target.value = '';
  };

  const handleCameraChange = event => {
    addFilesToPending(event.target.files);
    event.target.value = '';
  };

  const cancelAttachmentUpload = localId => {
    try {
      uploadXhrRefs.current?.[localId]?.abort?.();
      delete uploadXhrRefs.current[localId];

      setPendingAttachments(prev =>
        prev.map(file =>
          file.localId === localId
            ? {
                ...file,
                status: 'cancelled',
                progress: 0,
                error: 'Cancelled',
              }
            : file
        )
      );
    } catch (cancelError) {
      console.error('Cancel upload error:', cancelError);
    }
  };

  const removePendingAttachment = localId => {
    const item = pendingAttachments.find(file => file.localId === localId);

    if (item?.status === 'uploading') {
      const confirmCancel = window.confirm(
        'Ye image upload ho rahi hai. Cancel karni hai?'
      );

      if (confirmCancel) {
        cancelAttachmentUpload(localId);
      }

      return;
    }

    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }

    setPendingAttachments(prev =>
      prev.filter(file => file.localId !== localId)
    );
  };

  const uploadSingleAttachment = attachment => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      uploadXhrRefs.current[attachment.localId] = xhr;

      const formData = new FormData();

      formData.append('files', attachment.file);
      formData.append('chat_id', resolvedJobId);
      formData.append('job_id', resolvedJobId);
      formData.append('sender_id', adminId);
      formData.append('receiver_id', String(resolvedElectricianId));
      formData.append('type', 'admin_job_chat');

      xhr.open('POST', CHAT_UPLOAD_URL);

      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);

          setPendingAttachments(prev =>
            prev.map(file =>
              file.localId === attachment.localId
                ? {
                    ...file,
                    status: 'uploading',
                    progress,
                  }
                : file
            )
          );
        }
      };

      xhr.onload = () => {
        delete uploadXhrRefs.current[attachment.localId];

        try {
          const response = JSON.parse(xhr.responseText || '{}');

          if (xhr.status >= 200 && xhr.status < 300 && response?.success) {
            const uploadedFile = response.files?.[0];

            setPendingAttachments(prev =>
              prev.map(file =>
                file.localId === attachment.localId
                  ? {
                      ...file,
                      status: 'uploaded',
                      progress: 100,
                      serverUrl: uploadedFile?.url || '',
                    }
                  : file
              )
            );

            resolve(uploadedFile);
            return;
          }

          setPendingAttachments(prev =>
            prev.map(file =>
              file.localId === attachment.localId
                ? {
                    ...file,
                    status: 'error',
                    error: response?.message || 'Upload failed',
                  }
                : file
            )
          );

          reject(new Error(response?.message || 'Upload failed'));
        } catch (parseError) {
          setPendingAttachments(prev =>
            prev.map(file =>
              file.localId === attachment.localId
                ? {
                    ...file,
                    status: 'error',
                    error: 'Invalid upload response',
                  }
                : file
            )
          );

          reject(parseError);
        }
      };

      xhr.onerror = () => {
        delete uploadXhrRefs.current[attachment.localId];

        setPendingAttachments(prev =>
          prev.map(file =>
            file.localId === attachment.localId
              ? {
                  ...file,
                  status: 'error',
                  error: 'Network error',
                }
              : file
          )
        );

        reject(new Error('Network error'));
      };

      xhr.onabort = () => {
        delete uploadXhrRefs.current[attachment.localId];

        setPendingAttachments(prev =>
          prev.map(file =>
            file.localId === attachment.localId
              ? {
                  ...file,
                  status: 'cancelled',
                  progress: 0,
                  error: 'Cancelled',
                }
              : file
          )
        );

        reject(new Error('Upload cancelled'));
      };

      setPendingAttachments(prev =>
        prev.map(file =>
          file.localId === attachment.localId
            ? {
                ...file,
                status: 'uploading',
                progress: 0,
                error: '',
              }
            : file
        )
      );

      xhr.send(formData);
    });
  };

  const uploadPendingAttachments = async filesSnapshot => {
    const filesToUpload = filesSnapshot.filter(file => {
      return file.status === 'queued' || file.status === 'error';
    });

    const alreadyUploaded = filesSnapshot
      .filter(file => file.status === 'uploaded' && file.serverUrl)
      .map(file => ({
        url: file.serverUrl,
        name: file.name,
        type: file.type,
      }));

    if (filesToUpload.length === 0) {
      return alreadyUploaded;
    }

    const uploadedFiles = [];

    for (const attachment of filesToUpload) {
      const uploaded = await uploadSingleAttachment(attachment);

      uploadedFiles.push({
        url: uploaded?.url,
        name: uploaded?.originalName || uploaded?.filename || attachment.name,
        type: uploaded?.mimeType || attachment.type,
        filename: uploaded?.filename,
      });
    }

    return [...alreadyUploaded, ...uploadedFiles].filter(file => file.url);
  };

  const sendMessage = async event => {
    event.preventDefault();

    const cleanMessage = messageText.trim();

    const validPendingFiles = pendingAttachments.filter(file =>
      ['queued', 'uploaded', 'error'].includes(file.status)
    );

    const hasFiles = validPendingFiles.length > 0;

    if ((!cleanMessage && !hasFiles) || !resolvedJobId || sending) return;

    if (!resolvedElectricianId) {
      setError(
        'Electrician ID missing hai. ViewAppointmentLayer se electricianId pass karein.'
      );
      return;
    }

    setSending(true);
    setError(null);

    const now = new Date().toISOString();

    try {
      let uploadedAttachments = [];

      if (hasFiles) {
        uploadedAttachments = await uploadPendingAttachments(validPendingFiles);
      }

      const firstImage = uploadedAttachments.find(file =>
        String(file.type || '').startsWith('image/')
      );

      const messagePreview =
        cleanMessage ||
        (uploadedAttachments.length > 1
          ? `${uploadedAttachments.length} images`
          : uploadedAttachments.length === 1
          ? 'Image attachment'
          : 'message');

      const messageData = {
        _id: Date.now().toString(),
        text: cleanMessage,
        createdAt: now,
        user: {
          _id: adminId,
          role: 'admin',
          name: 'Admin',
        },
        receiver: {
          _id: String(resolvedElectricianId),
          role: 'electrician',
        },
        job_id: resolvedJobId,
        chat_type: 'admin_job_chat',
        type: 'admin_job_chat',
        status: 'sent',
        readBy: {
          [adminId]: true,
        },
        edited: false,
        deletedFor: {},
        deletedForEveryone: false,
        attachments: uploadedAttachments,
        image: firstImage?.url || '',
      };

      const messageRef = push(ref(db, chatPath));

      await set(messageRef, messageData);
      setMessageText('');
      setPendingAttachments([]);

      try {
        const response = await fetch(
          `${API_URL}/api/notification/send-notification`,
          {
            method: 'POST',
            headers: {
              Accept: '*/*',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              title: 'New Message Received From Admin',
              receiver_id: String(resolvedElectricianId),
              body: `Admin sent you ${messagePreview} for Job #${resolvedJobId}`,
              description: `Admin sent you ${messagePreview} for Job #${resolvedJobId}`,
              type: 'admin_job_chat',
              job_id: resolvedJobId,
            }),
          }
        );

        if (!response.ok) {
          console.error(
            'Admin job chat notification error:',
            await response.text()
          );
        }
      } catch (notificationError) {
        console.error('Notification error:', notificationError);
      }

      setTimeout(async () => {
        await update(messageRef, {
          status: 'delivered',
        });
      }, 800);
    } catch (sendError) {
      console.error('Send message error:', sendError);

      if (String(sendError?.message || '').toLowerCase().includes('cancel')) {
        setError('Upload cancel ho gayi.');
      } else {
        setError('Message send nahi ho saka. Console check karein.');
      }
    } finally {
      setSending(false);
    }
  };

  const deleteMessageForMe = async message => {
    if (!resolvedJobId || !message?.firebaseKey) return;

    const confirmDelete = window.confirm('Delete this message for admin only?');

    if (!confirmDelete) return;

    try {
      await update(ref(db, `${chatPath}/${message.firebaseKey}`), {
        [`deletedFor/${adminId}`]: true,
        deletedForAdminAt: new Date().toISOString(),
      });
    } catch (deleteError) {
      console.error('Delete for me error:', deleteError);
      setError('Message delete nahi ho saka.');
    }
  };

  const deleteMessageForEveryone = async message => {
    if (!resolvedJobId || !message?.firebaseKey) return;

    if (String(message?.user?._id) !== adminId) {
      alert('Aap sirf apna message everyone ke liye delete kar sakte hain.');
      return;
    }

    if (isDeletedForEveryone(message)) {
      alert('Message already deleted hai.');
      return;
    }

    const confirmDelete = window.confirm('Delete this message for everyone?');

    if (!confirmDelete) return;

    try {
      await update(ref(db, `${chatPath}/${message.firebaseKey}`), {
        text: '',
        image: '',
        attachments: [],
        deletedForEveryone: true,
        deletedForEveryoneAt: new Date().toISOString(),
        status: 'deleted',
      });
    } catch (deleteError) {
      console.error('Delete for everyone error:', deleteError);
      setError('Message delete nahi ho saka.');
    }
  };

  const renderAttachments = message => {
    const attachments = Array.isArray(message?.attachments)
      ? message.attachments
      : [];

    if (attachments.length === 0 && message?.image) {
      return (
        <div className="rw-job-chat-attachments single">
          <a href={message.image} target="_blank" rel="noreferrer">
            <img src={message.image} alt="Chat attachment" />
          </a>
        </div>
      );
    }

    if (attachments.length === 0) return null;

    return (
      <div
        className={`rw-job-chat-attachments ${
          attachments.length === 1 ? 'single' : ''
        }`}
      >
        {attachments.map((attachment, index) => {
          const imageUrl = attachment?.url || attachment?.uri || '';

          if (!imageUrl) return null;

          return (
            <a
              key={`${imageUrl}-${index}`}
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={imageUrl}
                alt={attachment.name || 'Chat attachment'}
              />
            </a>
          );
        })}
      </div>
    );
  };

  const renderPendingAttachments = () => {
    if (pendingAttachments.length === 0) return null;

    return (
      <div className="rw-chat-pending-wrap">
        {pendingAttachments.map(file => (
          <div className="rw-chat-pending-card" key={file.localId}>
            <img src={file.previewUrl} alt={file.name} />

            <button
              type="button"
              className="rw-chat-pending-remove"
              onClick={() => removePendingAttachment(file.localId)}
            >
              <Icon icon="mdi:close" />
            </button>

            {file.status === 'uploading' ? (
              <button
                type="button"
                className="rw-chat-pending-cancel"
                onClick={() => cancelAttachmentUpload(file.localId)}
              >
                Cancel
              </button>
            ) : null}

            <div className="rw-chat-progress-track">
              <div
                className={`rw-chat-progress-fill ${
                  file.status === 'error' || file.status === 'cancelled'
                    ? 'danger'
                    : ''
                }`}
                style={{ width: `${file.progress || 0}%` }}
              />
            </div>

            <div className="rw-chat-pending-status">
              {file.status === 'queued'
                ? 'Ready'
                : file.status === 'uploading'
                ? `${file.progress || 0}%`
                : file.status === 'uploaded'
                ? 'Uploaded'
                : file.status === 'cancelled'
                ? 'Cancelled'
                : 'Failed'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStatus = message => {
    if (String(message?.user?._id) !== adminId) return null;

    if (message?.status === 'read') {
      return <span className="rw-message-status read">✓✓ Read</span>;
    }

    if (message?.status === 'delivered') {
      return <span className="rw-message-status">✓✓ Delivered</span>;
    }

    if (message?.status === 'deleted') {
      return <span className="rw-message-status">Deleted</span>;
    }

    return <span className="rw-message-status">✓ Sent</span>;
  };

  const canSend =
    Boolean(messageText.trim()) ||
    pendingAttachments.some(file =>
      ['queued', 'uploaded', 'error'].includes(file.status)
    );

  return (
    <div className="rw-job-chat-shell">
      <style>
        {`
          .rw-job-chat-shell {
            height: calc(100vh - 155px);
            min-height: 620px;
            display: flex;
            flex-direction: column;
            background:
              radial-gradient(circle at top left, rgba(219, 111, 47, 0.08), transparent 30%),
              linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            border: 1px solid #e5e7eb;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
          }

          .rw-job-chat-header {
            padding: 16px 18px;
            background: #ffffff;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .rw-chat-user-wrap {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
          }

          .rw-chat-avatar {
            width: 44px;
            height: 44px;
            min-width: 44px;
            border-radius: 16px;
            background: #fff0e8;
            color: #db6f2f;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-weight: 900;
          }

          .rw-chat-title {
            margin: 0;
            color: #111827;
            font-size: 17px;
            font-weight: 900;
          }

          .rw-chat-subtitle {
            margin: 3px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 700;
          }

          .rw-job-chip {
            border-radius: 999px;
            background: #fff0e8;
            color: #db6f2f;
            border: 1px solid rgba(219, 111, 47, 0.2);
            padding: 8px 13px;
            font-size: 12px;
            font-weight: 900;
            white-space: nowrap;
          }

          .rw-chat-error {
            margin: 12px 16px 0;
          }

          .rw-job-chat-body {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
          }

          .rw-empty-chat {
            min-height: 360px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            color: #64748b;
            text-align: center;
          }

          .rw-empty-chat svg {
            color: #db6f2f;
            font-size: 52px;
            margin-bottom: 12px;
          }

          .rw-chat-message-row {
            display: flex;
            align-items: flex-end;
            gap: 10px;
            margin-bottom: 15px;
          }

          .rw-chat-message-row.right {
            justify-content: flex-end;
          }

          .rw-chat-message-row.left {
            justify-content: flex-start;
          }

          .rw-chat-bubble {
            max-width: 74%;
            padding: 12px 14px;
            border-radius: 18px;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
          }

          .rw-chat-message-row.left .rw-chat-bubble {
            background: #ffffff;
            color: #111827;
            border: 1px solid #eef2f7;
            border-bottom-left-radius: 6px;
          }

          .rw-chat-message-row.right .rw-chat-bubble {
            background: linear-gradient(135deg, #db6f2f 0%, #b95018 100%);
            color: #ffffff;
            border-bottom-right-radius: 6px;
          }

          .rw-chat-message-row.right .rw-chat-text,
          .rw-chat-message-row.right .rw-chat-meta,
          .rw-chat-message-row.right .rw-message-status {
            color: #ffffff;
          }

          .rw-chat-text {
            margin: 0 0 8px;
            line-height: 1.55;
            word-break: break-word;
            font-size: 14px;
          }

          .rw-chat-meta {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            color: #64748b;
            font-size: 11px;
            opacity: 0.9;
          }

          .rw-message-status.read {
            color: #0ea5e9;
            font-weight: 800;
          }

          .rw-job-chat-attachments {
            display: grid;
            grid-template-columns: repeat(2, minmax(100px, 1fr));
            gap: 8px;
            margin-bottom: 10px;
          }

          .rw-job-chat-attachments.single {
            grid-template-columns: 1fr;
          }

          .rw-job-chat-attachments img {
            width: 100%;
            max-width: 220px;
            height: 145px;
            object-fit: cover;
            border-radius: 12px;
            display: block;
          }

          .rw-chat-actions {
            position: relative;
            display: inline-flex;
            align-items: center;
          }

          .rw-chat-action-btn {
            border: 0;
            background: transparent;
            color: inherit;
            width: 26px;
            height: 26px;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .rw-chat-action-btn:hover {
            background: rgba(15, 23, 42, 0.08);
          }

          .rw-chat-message-row.right .rw-chat-action-btn:hover {
            background: rgba(255, 255, 255, 0.18);
          }

          .rw-chat-action-menu {
            min-width: 190px;
          }

          .rw-chat-pending-wrap {
            display: flex;
            gap: 10px;
            overflow-x: auto;
            padding: 10px 14px;
            background: #ffffff;
            border-top: 1px solid #eef2f7;
          }

          .rw-chat-pending-card {
            width: 108px;
            min-width: 108px;
            border-radius: 14px;
            border: 1px solid #e5e7eb;
            background: #f8fafc;
            padding: 7px;
            position: relative;
          }

          .rw-chat-pending-card img {
            width: 92px;
            height: 82px;
            border-radius: 11px;
            object-fit: cover;
            display: block;
            background: #e5e7eb;
          }

          .rw-chat-pending-remove {
            position: absolute;
            top: 2px;
            right: 2px;
            width: 24px;
            height: 24px;
            border-radius: 999px;
            background: #dc2626;
            color: #ffffff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 0;
          }

          .rw-chat-pending-cancel {
            position: absolute;
            left: 10px;
            right: 10px;
            bottom: 30px;
            height: 22px;
            border-radius: 999px;
            border: 0;
            background: rgba(15, 23, 42, 0.76);
            color: #ffffff;
            font-size: 10px;
            font-weight: 800;
          }

          .rw-chat-progress-track {
            height: 5px;
            background: #e5e7eb;
            border-radius: 999px;
            margin-top: 7px;
            overflow: hidden;
          }

          .rw-chat-progress-fill {
            height: 5px;
            background: #db6f2f;
            border-radius: 999px;
            transition: width 0.18s ease;
          }

          .rw-chat-progress-fill.danger {
            background: #dc2626;
          }

          .rw-chat-pending-status {
            margin-top: 4px;
            font-size: 10px;
            color: #64748b;
            text-align: center;
            font-weight: 800;
          }

          .rw-chat-form {
            padding: 14px;
            background: #ffffff;
            border-top: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .rw-chat-form input[type='text'] {
            flex: 1;
            height: 48px;
            border-radius: 999px;
            border: 1px solid #e5e7eb;
            background: #f9fafb;
            padding: 0 18px;
            outline: none;
          }

          .rw-chat-form input[type='text']:focus {
            background: #ffffff;
            border-color: #db6f2f;
            box-shadow: 0 0 0 4px rgba(219, 111, 47, 0.08);
          }

          .rw-hidden-file-input {
            display: none;
          }

          .rw-chat-attach-btn {
            width: 44px;
            height: 44px;
            border-radius: 14px;
            border: 1px solid #fee2d3;
            background: #fff7f1;
            color: #db6f2f;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            transition: all 0.15s ease;
          }

          .rw-chat-attach-btn:hover {
            background: #fff0e8;
            transform: translateY(-1px);
          }

          .rw-chat-attach-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }

          .rw-send-btn {
            height: 44px;
            min-width: 96px;
            border: 0;
            border-radius: 999px;
            background: #db6f2f;
            color: #ffffff;
            font-weight: 900;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            padding: 0 16px;
          }

          .rw-send-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          @media (max-width: 768px) {
            .rw-chat-bubble {
              max-width: 86%;
            }

            .rw-job-chat-attachments {
              grid-template-columns: 1fr;
            }

            .rw-chat-form {
              flex-wrap: wrap;
            }

            .rw-chat-form input[type='text'] {
              min-width: 100%;
              order: -1;
            }
          }
        `}
      </style>

      <div className="rw-job-chat-header">
        <div className="rw-chat-user-wrap">
          <div className="rw-chat-avatar">EL</div>

          <div className="min-w-0">
            <h6 className="rw-chat-title">{electricianName}</h6>
            <p className="rw-chat-subtitle">
              {resolvedElectricianId
                ? `Electrician ID: ${resolvedElectricianId}`
                : 'Electrician ID not found yet'}
            </p>
          </div>
        </div>

        <div className="rw-job-chip">Job #{resolvedJobId}</div>
      </div>

      {error ? (
        <div className="alert alert-danger rw-chat-error" role="alert">
          {error}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setError(null)}
            aria-label="Close"
          />
        </div>
      ) : null}

      <div className="rw-job-chat-body" ref={messageListRef}>
        {loading ? (
          <div className="rw-empty-chat">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 mb-0">Loading job chat...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="rw-empty-chat">
            <Icon icon="solar:chat-round-dots-linear" />
            <h6>No messages yet</h6>
            <p className="mb-0">
              Is job ke related chat yahan show hogi. Admin reply send kar sakta hai.
            </p>
          </div>
        ) : (
          messages.map(message => {
            const isAdminMessage = String(message?.user?._id) === adminId;
            const isDeletedEveryone = isDeletedForEveryone(message);

            return (
              <div
                key={message.firebaseKey || message._id}
                className={`rw-chat-message-row ${
                  isAdminMessage ? 'right' : 'left'
                }`}
              >
                {!isAdminMessage ? (
                  <div className="rw-chat-avatar">EL</div>
                ) : null}

                <div className="rw-chat-bubble">
                  {!isDeletedEveryone ? renderAttachments(message) : null}

                  <p
                    className={`rw-chat-text ${
                      isDeletedEveryone ? 'fst-italic text-secondary' : ''
                    }`}
                  >
                    {getVisibleText(message) ||
                      (!isDeletedEveryone ? 'Attachment' : '')}

                    {message?.edited && !isDeletedEveryone ? (
                      <span className="ms-2 text-xs">(edited)</span>
                    ) : null}
                  </p>

                  <div className="rw-chat-meta">
                    <span>
                      {formatDate(message.createdAt)}
                      {renderStatus(message)}
                    </span>

                    <div className="btn-group rw-chat-actions">
                      <button
                        type="button"
                        className="rw-chat-action-btn"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <Icon icon="tabler:dots-vertical" />
                      </button>

                      <ul className="dropdown-menu dropdown-menu-end border rw-chat-action-menu">
                        <li>
                          <button
                            type="button"
                            className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                            onClick={() => deleteMessageForMe(message)}
                          >
                            <Icon icon="solar:trash-bin-trash-linear" />
                            Delete for me
                          </button>
                        </li>

                        {isAdminMessage && !isDeletedEveryone ? (
                          <li>
                            <button
                              type="button"
                              className="dropdown-item rounded text-danger bg-hover-danger-100 d-flex align-items-center gap-2"
                              onClick={() => deleteMessageForEveryone(message)}
                            >
                              <Icon icon="solar:trash-bin-minimalistic-linear" />
                              Delete for everyone
                            </button>
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {renderPendingAttachments()}

      <form className="rw-chat-form" onSubmit={sendMessage}>
        <input
          ref={cameraInputRef}
          className="rw-hidden-file-input"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCameraChange}
        />

        <input
          ref={galleryInputRef}
          className="rw-hidden-file-input"
          type="file"
          accept="image/*"
          multiple
          onChange={handleGalleryChange}
        />

        <button
          type="button"
          className="rw-chat-attach-btn"
          disabled={sending}
          title="Camera"
          onClick={() => cameraInputRef.current?.click()}
        >
          <Icon icon="solar:camera-linear" />
        </button>

        <button
          type="button"
          className="rw-chat-attach-btn"
          disabled={sending}
          title="Gallery"
          onClick={() => galleryInputRef.current?.click()}
        >
          <Icon icon="solar:gallery-linear" />
        </button>

        <input
          type="text"
          placeholder={`Reply for Job #${resolvedJobId}`}
          value={messageText}
          onChange={event => setMessageText(event.target.value)}
        />

        <button
          type="submit"
          className="rw-send-btn"
          disabled={sending || !canSend}
        >
          {sending ? 'Sending...' : 'Send'}
          <Icon icon="f7:paperplane" />
        </button>
      </form>
    </div>
  );
};

ChatHistory.propTypes = {
  jobId: PropTypes.string.isRequired,
  senderId: PropTypes.string,
  electricianId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  electricianName: PropTypes.string,
};

export default ChatHistory;